import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { performanceMonitor } from '@/lib/utils/performance-monitor';
import { TrendingUp, Clock, Database, Activity } from 'lucide-react';

export function PerformanceStatus() {
  const [summary, setSummary] = React.useState<any>(null);
  const [trends, setTrends] = React.useState<any>(null);

  React.useEffect(() => {
    const updatePerformance = () => {
      const performanceSummary = performanceMonitor.getPerformanceSummary();
      const recentTrends = performanceMonitor.getRecentTrends(5);
      setSummary(performanceSummary);
      setTrends(recentTrends);
    };



    // Update performance immediately
    updatePerformance();

    // Update performance every 10 seconds
    const interval = setInterval(updatePerformance, 10000);

    return () => clearInterval(interval);
  }, []);

  if (!summary || summary.totalUpdates === 0) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Performance Metrics
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-center py-4">
            <Activity className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              No performance data yet
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Metrics will appear as real-time updates occur
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatUptime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <TrendingUp className="h-4 w-4" />
          Performance Metrics
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Total Updates:</span>
          <Badge variant="outline" className="text-xs">
            {summary.totalUpdates}
          </Badge>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Avg Response Time:</span>
          <Badge variant="outline" className="text-xs">
            {summary.averageResponseTime}ms
          </Badge>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Avg Data Transfer:</span>
          <Badge variant="outline" className="text-xs">
            {formatBytes(summary.averageDataTransfer)}
          </Badge>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Update Frequency:</span>
          <Badge variant="outline" className="text-xs">
            {summary.averageUpdateFrequency}/s
          </Badge>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Total Data:</span>
          <Badge variant="outline" className="text-xs">
            {formatBytes(summary.totalDataTransfer)}
          </Badge>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Uptime:</span>
          <Badge variant="outline" className="text-xs">
            {formatUptime(summary.uptime)}
          </Badge>
        </div>

        {trends && (
          <div className="pt-2 border-t">
            <div className="text-xs text-muted-foreground mb-2">Recent (5min):</div>
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span>Updates:</span>
                <span>{trends.recentUpdates}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span>Avg Response:</span>
                <span>{trends.recentAverageResponseTime}ms</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span>Avg Data:</span>
                <span>{formatBytes(trends.recentAverageDataTransfer)}</span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
} 